import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useCategories } from '../contexts/CategoriesContext';
import NotesField from './NotesField';

/**
 * NotesTimeline component for displaying multiple notes with user attribution and timestamps
 */
const NotesTimeline = ({ notes = [], onAddNote, onEditNote, onDeleteNote, readOnly = false }) => {
  const { user } = useAuth();
  const { categories } = useCategories();
  const theme = useTheme();
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState('');
  const [showNewNoteForm, setShowNewNoteForm] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedCategory, setEditedCategory] = useState('');
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Get flat list of all categories for dropdown
  const categoryOptions = categories.flatMap(group => {
    return group.items.map(item => ({
      id: item.fieldName,
      name: item.title,
      group: group.title
    }));
  });

  // Sort notes by updated timestamp (newest first)
  const sortedNotes = [...notes]
    .sort((a, b) => {
      // Handle Firestore timestamps or regular Date objects
      const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt?.getTime() || 0);
      const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt?.getTime() || 0);
      return bTime - aTime;
    });

  const handleAddNoteSubmit = () => {
    if (!newNoteContent.trim()) return;
    
    const newNote = {
      id: Date.now().toString(), // Simple unique ID
      content: newNoteContent,
      categoryId: newNoteCategory || '',
      createdBy: user.email,
      createdAt: new Date(),
      updatedAt: new Date(),
      user: {
        id: user.uid,
        name: user.displayName || user.email.split('@')[0],
        avatar: user.photoURL
      }
    };
    
    onAddNote(newNote);
    setNewNoteContent('');
    setNewNoteCategory('');
    setShowNewNoteForm(false);
  };

  const handleEditSubmit = (noteId) => {
    if (!editedContent.trim()) return;
    
    onEditNote(noteId, {
      content: editedContent,
      categoryId: editedCategory,
      updatedAt: new Date()
    });
    
    setEditingNoteId(null);
    setEditedContent('');
    setEditedCategory('');
  };

  const startEditing = (note) => {
    setEditingNoteId(note.id);
    setEditedContent(note.content);
    setEditedCategory(note.categoryId || '');
  };

  const handleDeleteConfirm = () => {
    if (noteToDelete) {
      onDeleteNote(noteToDelete.id);
    }
    setDeleteDialogOpen(false);
  };

  // Function to get category name by ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const category = categoryOptions.find(cat => cat.id === categoryId);
    return category ? category.name : null;
  };

  // Function to get initials from email or name
  const getInitials = (email, userName) => {
    if (userName) {
      const parts = userName.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return userName.substring(0, 2).toUpperCase();
    }
    
    if (!email) return '?';
    const username = email.split('@')[0];
    const parts = username.split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  };

  // Function to format user name from email or use provided name
  const formatUserName = (email, userName) => {
    if (userName) return userName;
    if (!email) return 'Unknown';
    
    const username = email.split('@')[0];
    const parts = username.split('.');
    
    if (parts.length >= 2) {
      return parts.map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ');
    }
    
    return username;
  };

  // Function to format date in a user-friendly way
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : date.toDate();
    return format(dateObj, 'MMM d, yyyy'); // More compact format: "Apr 29, 2023"
  };

  // Function to format time only
  const formatTime = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : date.toDate();
    return format(dateObj, 'h:mm a'); // Format: "11:30 AM"
  };

  // Function to generate a gradient based on email
  const getGradientForEmail = (email) => {
    if (!email) return `linear-gradient(45deg, #607d8b, #78909c)`;
    
    // Simple hash function to generate consistent colors for same email
    const hash = email.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    // Use a limited palette that aligns with the design
    const colors = [
      ['#FF4D4F', '#FF7875'], // Red
      ['#2196F3', '#64B5F6'], // Blue
      ['#4CAF50', '#81C784'], // Green
      ['#FF9800', '#FFB74D'], // Orange
      ['#9C27B0', '#BA68C8'], // Purple
      ['#00BCD4', '#4DD0E1'], // Cyan
      ['#FF5722', '#FF8A65'], // Deep Orange
      ['#795548', '#A1887F'], // Brown
    ];
    
    const colorIndex = Math.abs(hash) % colors.length;
    return `linear-gradient(135deg, ${colors[colorIndex][0]}, ${colors[colorIndex][1]})`;
  };

  // Function to generate a color based on category
  const getCategoryColor = (categoryId) => {
    if (!categoryId) return null;
    
    // Simple hash function to generate consistent colors
    const hash = categoryId.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    // Color palette for categories (different from user gradients)
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      '#4A148C', // deep purple
      '#880E4F', // deep pink
      '#01579B', // light blue
      '#004D40', // teal
      '#1B5E20', // green
      '#F57F17', // amber
      '#BF360C', // deep orange
    ];
    
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* New note form */}
      {showNewNoteForm && !readOnly && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.3),
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            boxShadow: `0 3px 10px ${alpha(theme.palette.common.black, 0.05)}`
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                display: 'inline-block',
                width: 4,
                height: 18,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
                marginRight: 1.5
              }
            }}
          >
            Add New Note
          </Typography>
          
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="new-note-category-label">Category (Optional)</InputLabel>
            <Select
              labelId="new-note-category-label"
              value={newNoteCategory}
              label="Category (Optional)"
              onChange={(e) => setNewNoteCategory(e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme => alpha(theme.palette.primary.main, 0.3),
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: theme => alpha(theme.palette.primary.main, 0.5),
                      }
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    }
                  }
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.2)
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5)
                }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {categories.map((group) => [
                <MenuItem 
                  key={group.id} 
                  disabled 
                  sx={{ 
                    opacity: 0.8, 
                    fontWeight: 600,
                    pointerEvents: 'none',
                    fontSize: '0.75rem',
                    color: theme.palette.text.secondary,
                    py: 0.5,
                    px: 1.5
                  }}
                >
                  {group.title}
                </MenuItem>,
                ...group.items.map((item) => {
                  // Generate a stable color for the category
                  const categoryColor = getCategoryColor(item.fieldName);
                  return (
                    <MenuItem 
                      key={item.fieldName} 
                      value={item.fieldName}
                      sx={{ 
                        pl: 2.5,
                        py: 0.5,
                        minHeight: 'auto',
                        fontSize: '0.8rem',
                        '&:hover': {
                          backgroundColor: alpha(categoryColor, 0.04),
                        },
                        '&.Mui-selected': {
                          backgroundColor: alpha(categoryColor, 0.08),
                          '&:hover': {
                            backgroundColor: alpha(categoryColor, 0.12),
                          }
                        }
                      }}
                    >
                      {item.title}
                    </MenuItem>
                  );
                })
              ])}
            </Select>
          </FormControl>
          
          <Box 
            sx={{ 
              border: '1px solid',
              borderColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 1,
              bgcolor: theme.palette.background.paper,
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.5)
              },
              mb: 2.5
            }}
          >
            <NotesField
              initialValue={newNoteContent}
              onChange={setNewNoteContent}
              isEditing={true}
              placeholder="Enter your note here..."
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                setShowNewNoteForm(false);
                setNewNoteContent('');
                setNewNoteCategory('');
              }}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                px: 2
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small"
              disabled={!newNoteContent.trim()}
              onClick={handleAddNoteSubmit}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                boxShadow: 1,
                px: 2.5,
                '&:hover': {
                  boxShadow: 2
                }
              }}
            >
              Save Note
            </Button>
          </Box>
        </Paper>
      )}

      {/* Note list */}
      {sortedNotes.length > 0 ? (
        <Box sx={{ mb: 1.5 }}>
          {sortedNotes.map((note, index) => {
            // Generate consistent avatar background gradient based on email
            const gradientBg = getGradientForEmail(note.createdBy);
            
            // Get category information if available
            const categoryName = getCategoryName(note.categoryId);

            return (
              <Paper
                key={note.id}
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  ...(note.id === 'legacy-note' && {
                    border: '1px dashed',
                    borderColor: theme => alpha(theme.palette.warning.main, 0.3),
                  }),
                  '&:hover': {
                    boxShadow: theme => `0 3px 8px ${alpha(theme.palette.common.black, 0.08)}`
                  }
                }}
              >
                {/* Note header */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  pl: 2,
                  position: 'relative'
                }}>
                  <Avatar 
                    src={note.user?.avatar}
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      fontSize: '1rem',
                      background: gradientBg,
                      fontWeight: 'bold',
                      color: 'white',
                      boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.15)}`
                    }}
                  >
                    {getInitials(note.createdBy, note.user?.name)}
                  </Avatar>
                  
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 0.5
                  }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem', 
                        lineHeight: 1.2
                      }}
                    >
                      {formatUserName(note.createdBy, note.user?.name)}
                    </Typography>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '0.7rem',
                        lineHeight: 1.2
                      }}
                    >
                      {formatDate(note.updatedAt)} {formatTime(note.updatedAt)}
                    </Typography>
                    
                                         {note.id === 'legacy-note' && (
                       <Typography
                         component="span"
                         variant="caption"
                         sx={{
                           px: 1,
                           py: 0.2,
                           backgroundColor: alpha(theme.palette.warning.main, 0.12),
                           color: theme.palette.warning.dark,
                           borderRadius: 1,
                           fontSize: '0.65rem',
                           fontWeight: 500
                         }}
                       >
                         LEGACY
                       </Typography>
                     )}
                  </Box>
                  
                  {!readOnly && (
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                      <Tooltip title="Edit Note">
                        <IconButton 
                          size="small"
                          onClick={() => startEditing(note)}
                          sx={{ 
                            p: 0.5,
                            color: 'action.active',
                            opacity: 0.7,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                        >
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete Note">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setNoteToDelete(note);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{ 
                            p: 0.5,
                            color: 'error.main',
                            opacity: 0.5,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: alpha(theme.palette.error.main, 0.08)
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                
                {/* Note content */}
                {editingNoteId === note.id ? (
                  <Box sx={{ p: 2, pt: 0 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id="edit-note-category-label">Category (Optional)</InputLabel>
                      <Select
                        labelId="edit-note-category-label"
                        value={editedCategory}
                        label="Category (Optional)"
                        onChange={(e) => setEditedCategory(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              overflowY: 'auto',
                              '&::-webkit-scrollbar': {
                                width: '8px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.3),
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.5),
                                }
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                              }
                            }
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {categories.map((group) => [
                          <MenuItem 
                            key={group.id} 
                            disabled 
                            sx={{ 
                              opacity: 0.8, 
                              fontWeight: 600,
                              pointerEvents: 'none',
                              fontSize: '0.75rem',
                              color: theme.palette.text.secondary,
                              py: 0.5,
                              px: 1.5
                            }}
                          >
                            {group.title}
                          </MenuItem>,
                          ...group.items.map((item) => {
                            // Generate a stable color for the category
                            const categoryColor = getCategoryColor(item.fieldName);
                            return (
                              <MenuItem 
                                key={item.fieldName} 
                                value={item.fieldName}
                                sx={{ 
                                  pl: 2.5,
                                  py: 0.5,
                                  minHeight: 'auto',
                                  fontSize: '0.8rem',
                                  '&:hover': {
                                    backgroundColor: alpha(categoryColor, 0.04),
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: alpha(categoryColor, 0.08),
                                    '&:hover': {
                                      backgroundColor: alpha(categoryColor, 0.12),
                                    }
                                  }
                                }}
                              >
                                {item.title}
                              </MenuItem>
                            );
                          })
                        ])}
                      </Select>
                    </FormControl>
                    
                    <NotesField
                      initialValue={editedContent}
                      onChange={setEditedContent}
                      isEditing={true}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditedContent('');
                          setEditedCategory('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained" 
                        size="small"
                        disabled={!editedContent.trim()}
                        onClick={() => handleEditSubmit(note.id)}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                    {/* Category headline above note text */}
                    {categoryName && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          mb: 1,
                          opacity: 0.8
                        }}
                      >
                        {categoryName}
                      </Typography>
                    )}
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        color: 'text.primary',
                        fontSize: '0.875rem'
                      }}
                    >
                      {note.content}
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 1,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
            border: '1px dashed',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No notes have been added yet
          </Typography>
        </Paper>
      )}
      
      {/* Add Note button at the bottom */}
      {!readOnly && !showNewNoteForm && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mt: 2 
        }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowNewNoteForm(true)}
            sx={{ 
              borderRadius: 2,
              boxShadow: 'none',
              borderStyle: 'dashed',
              '&:hover': {
                borderStyle: 'dashed',
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Add Note
          </Button>
        </Box>
      )}
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme => `
              0 24px 48px ${alpha(theme.palette.common.black, 0.15)},
              0 8px 16px ${alpha(theme.palette.common.black, 0.1)}
            `,
            // Enhanced glassmorphism effect
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: theme => theme.palette.mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
              pointerEvents: 'none',
              zIndex: -1
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Avatar sx={{ 
            bgcolor: theme => alpha(theme.palette.error.main, 0.1),
            color: 'error.main',
            width: 40,
            height: 40
          }}>
            <DeleteIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 400,
              fontFamily: 'StrawberrySansDisplay',
              letterSpacing: '-0.02em'
            }}>
              Delete Note
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                opacity: 0.8,
                fontFamily: 'StrawberrySansText'
              }}
            >
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2.5 }}>
          <Typography 
            variant="body2"
            sx={{
              fontFamily: 'StrawberrySansText',
              lineHeight: 1.6,
              color: 'text.primary'
            }}
          >
            Are you sure you want to delete this note? All information associated with this note will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.common.black, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 500,
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme => `0 6px 16px ${alpha(theme.palette.error.main, 0.4)}`
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesTimeline; 